import * as qs from 'qs';
import type { ReportCategory, ReportURLParams } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';
import { reportTypeDefaultFormatMap, reportTypeFormatMap } from '../shared/constants';
import { buildIncludeParams } from '../shared/utils';
import { ReportCategories } from '../../ReportGeneration/utils';
import type { FixPlansParams } from '../../../../../../shared/types/routes/fixPlans';

const getEndpoint = ({
  groupId,
  releaseId,
  reportType,
  format,
  query,
  includeOptions,
  dependencyMetadataOptions,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  query: {
    preview?: boolean;
    download?: boolean;
    isPublishing?: boolean;
    emails?: string;
  };
}) => {
  const isVulnerability = reportType === ReportCategories.Vulnerability;
  const defaultFileFormat = isVulnerability ? 'security' : reportTypeDefaultFormatMap[reportType];
  const formatQuery = format ? reportTypeFormatMap[format] : defaultFileFormat;
  const urlParams: ReportURLParams = {
    ...query,
    ...buildIncludeParams(includeOptions),
    dependencyInfoOptions: dependencyMetadataOptions,
  };
  // Security reports do not have a format query parameter. The response is always in PDF format.
  if (formatQuery !== 'security') {
    urlParams.format = formatQuery;
  }
  const queryString = qs.stringify(urlParams);

  return `/api/project_group/${groupId}/release/${releaseId}/attribution/${formatQuery}?${queryString}`;
};

const getFixPlansEndpoint = ({
  groupId,
  releaseId,
  format,
  query,
}: {
  groupId: number;
  releaseId: number;
  format: FormatName | null;
  query: FixPlansParams;
}) => {
  const queryString = qs.stringify({
    ...query,
    format: format ? reportTypeFormatMap[format] : reportTypeDefaultFormatMap[ReportCategories.FixPlan],
  });

  return `/api/project_group/${groupId}/release/${releaseId}/fix-plans?${queryString}`;
};

/**
 * Get the preview URL for a report at the release group level
 */
export const getPreviewURL = ({
  groupId,
  releaseId,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  if (reportType === ReportCategories.FixPlan) {
    return getFixPlansEndpoint({
      groupId,
      releaseId,
      format,
      query: {
        preview: true,
      },
    });
  }
  return getEndpoint({
    groupId,
    releaseId,
    format,
    includeOptions,
    dependencyMetadataOptions,
    query: {
      preview: true,
    },
    reportType,
  });
};

/**
 * Get the download URL for a report at the release group level
 */
export const getEmailURL = ({
  groupId,
  releaseId,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
  emails,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  emails: string;
}) => {
  return getEndpoint({
    groupId,
    releaseId,
    format,
    includeOptions,
    dependencyMetadataOptions,
    query: {
      emails,
    },
    reportType,
  });
};

/**
 * Get the URL to publish a report to the SBOM portal.
 */
export const getPublishURL = ({
  groupId,
  releaseId,
  reportType,
  format,
  includeOptions,
  dependencyMetadataOptions,
}: {
  groupId: number;
  releaseId: number;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  return getEndpoint({
    groupId,
    releaseId,
    format,
    includeOptions,
    dependencyMetadataOptions,
    query: {
      isPublishing: true,
    },
    reportType,
  });
};

/**
 * Get the download URL for a report at the release group level.
 * At this moment, the download feature is only available for vulnerability reports at the release group level.
 */
export const getDownloadVulnerabilityReportURL = ({
  groupId,
  releaseId,
  includeOptions,
  dependencyMetadataOptions,
}: {
  groupId: number;
  releaseId: number;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
}) => {
  return getEndpoint({
    groupId,
    releaseId,
    includeOptions,
    dependencyMetadataOptions,
    format: null, // Vulnerability reports format is not configurable
    query: {
      download: true,
    },
    reportType: 'vulnerability',
  });
};

/**
 * Get the download URL for a report at the release group level.
 * At this moment, the download feature is only available for vulnerability reports at the release group level.
 */
export const getDownloadFixPlansReportURL = ({
  groupId,
  releaseId,
  format,
}: {
  groupId: number;
  releaseId: number;
  format: FormatName | null;
}) => {
  return getFixPlansEndpoint({
    groupId,
    releaseId,
    format,
    query: { bundle: true },
  });
};

/**
 * Get the SBOM portal type based on the format of the report.
 */
export const getSBOMPortalReportType = (
  reportType: ReportCategory,
  format: FormatName | null,
): 'SBOM' | 'Attribution' | null => {
  if (reportType === 'sbom') {
    return 'SBOM';
  }
  if (reportType === 'licensing' && ['Plain Text'].includes(format || '')) {
    return 'Attribution';
  }

  return null;
};
